import { type KeyboardEvent, type MouseEvent, useEffect, useRef, useState } from "react";
import {
  AtSign,
  CalendarDaysIcon,
  Check,
  CheckSquare,
  ChevronDown,
  DollarSignIcon,
  Hash,
  AlignLeft,
  Loader2,
  Search,
  SmartphoneIcon,
  Tags,
  SearchX,
  Text,
  TextCursorInput,
  ToggleRight,
  X,
} from "lucide-react";
import Image from "next/image";
import Select, {
  ClassNamesConfig,
  ClearIndicatorProps,
  CSSObjectWithLabel,
  components,
  DropdownIndicatorProps,
  GroupBase,
  GroupHeadingProps,
  InputActionMeta,
  MenuProps,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  NoticeProps,
  OptionProps,
  Props,
  StylesConfig,
} from "react-select";
import Async, { AsyncProps } from "react-select/async";
import Creatable from "react-select/creatable";
import { IntegrationProviderId } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { customerEventSourceLabel } from "@/lib/constants/lists";
import { cn, formatPhoneNumber, getCustomerName } from "@/lib/utils";
import { CustomerAvatar } from "@/modules/customer/customer-avatar";
import { useScrollLockPassthrough } from "./scroll-lock-passthrough";

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDown className="shrink-0 w-4 h-4 ml-2 opacity-50" />
    </components.DropdownIndicator>
  );
};

const ClearIndicator = (props: ClearIndicatorProps) => {
  return (
    <components.ClearIndicator {...props}>
      <X className="shrink-0 w-4 h-4 ml-2 opacity-50" />
    </components.ClearIndicator>
  );
};

const MultiValueRemove = (props: MultiValueRemoveProps) => {
  return (
    <components.MultiValueRemove {...props}>
      <X className="h-2.5 w-2.5 shrink-0" />
    </components.MultiValueRemove>
  );
};

export const IntegrationIcon = (props: {
  integration?: IntegrationProviderId;
  className?: string;
}) => {
  const id = String(props.integration ?? "");
  const integration = id === "helios-pixel" || id === "Website" ? "helios" : props.integration;
  if (integration) {
    return (
      <Image
        alt={props.integration ?? integration}
        src={`/images/integrations/${integration}/icon.png`}
        width={200}
        height={200}
        className={cn("shrink-0 w-4 h-4 rounded", props.className)}
      />
    );
  } else {
    return <div className="shrink-0 w-4 h-4 bg-muted-foreground rounded" />;
    // return null
  }
};

export const TypeIcon = ({ type, className = "" }: { type: string; className?: string }) => {
  if (type === "date" || type === "date-picker") {
    return (
      <CalendarDaysIcon
        className={cn("w-3.5 h-3.5 shrink-0 mr-1.5 text-muted-foreground", className)}
      />
    );
  } else if (type === "number") {
    return <Hash className={cn("w-3.5 h-3.5 shrink-0 mr-1.5 text-muted-foreground", className)} />;
  } else if (type === "currency") {
    return (
      <DollarSignIcon
        className={cn("w-3.5 h-3.5 shrink-0 mr-1.5 text-muted-foreground", className)}
      />
    );
  } else if (type === "array") {
    return <Tags className={cn("w-3.5 h-3.5 shrink-0 mr-1.5 text-muted-foreground", className)} />;
  } else if (type === "enum" || type === "select") {
    return (
      <AlignLeft className={cn("w-3.5 h-3.5 shrink-0 mr-1.5 text-muted-foreground", className)} />
    );
  } else if (type === "boolean") {
    return (
      <ToggleRight className={cn("w-3.5 h-3.5 shrink-0 mr-1.5 text-muted-foreground", className)} />
    );
  } else if (type === "checkbox") {
    return (
      <CheckSquare className={cn("w-3.5 h-3.5 shrink-0 mr-1.5 text-muted-foreground", className)} />
    );
  } else if (type === "textarea") {
    return <Text className={cn("w-3.5 h-3.5 shrink-0 mr-1.5 text-muted-foreground", className)} />;
  } else if (type === "string" || type === "input") {
    return (
      <TextCursorInput
        className={cn("w-3.5 h-3.5 shrink-0 mr-1.5 text-muted-foreground", className)}
      />
    );
  } else if (type === "phone") {
    return (
      <SmartphoneIcon
        className={cn("w-3.5 h-3.5 shrink-0 mr-1.5 text-muted-foreground", className)}
      />
    );
  } else if (type === "email") {
    return (
      <AtSign className={cn("w-3.5 h-3.5 shrink-0 mr-1.5 text-muted-foreground", className)} />
    );
  } else {
    return null;
  }
};

const GroupHeading = (props: GroupHeadingProps) => {
  // `plainGroupHeading` opts out of the integration-source chrome (icon +
  // source label) for selects whose group labels are plain category names
  // rather than integration provider ids. Without it those labels resolve
  // to a broken `/images/integrations/<label>/icon.png`.
  const plain = (props.selectProps as { plainGroupHeading?: boolean }).plainGroupHeading;
  const label = String(props.data.label ?? "");
  return (
    <components.GroupHeading {...props}>
      <div className="flex items-center gap-2">
        {plain ? null : (
          <IntegrationIcon integration={label as IntegrationProviderId} className="h-3.5 w-3.5" />
        )}
        <span className="text-xs font-semibold text-foreground">
          {plain ? label : customerEventSourceLabel(label)}
        </span>
        <Badge variant="secondary" className="ml-auto text-[10px] font-normal">
          {props.data.options.length}
        </Badge>
      </div>
    </components.GroupHeading>
  );
};

/**
 * True when this select renders as a multi-pick chip cell (segment "is any
 * of" style pickers). Those menus get extra affordances — checkbox option
 * prefixes, an instructional header with a close button, and a selected
 * count footer — that app-wide single selects don't want.
 */
function isChipMultiSelect(selectProps: unknown): boolean {
  const sp = selectProps as { variant?: "default" | "chip"; isMulti?: boolean };
  return sp.variant === "chip" && !!sp.isMulti;
}

function isChipVariant(selectProps: unknown): boolean {
  return (selectProps as { variant?: "default" | "chip" }).variant === "chip";
}

/**
 * Anchor for shift-click range selection in chip multi menus: holds the
 * `getOptionValue` of the last plainly-clicked option. Created per select
 * instance by the wrappers below and threaded through `selectProps` (react-
 * select forwards unknown props), since the shared `Option` component has no
 * instance state of its own. Cleared when the menu unmounts so a re-opened
 * menu can't range-select from a stale anchor.
 */
type ShiftRangeAnchorRef = { current: string | null };

function getShiftRangeAnchorRef(selectProps: unknown): ShiftRangeAnchorRef | undefined {
  return (selectProps as { shiftRangeAnchorRef?: ShiftRangeAnchorRef }).shiftRangeAnchorRef;
}

/**
 * The flat list of options currently visible in the menu, in render order:
 * groups flattened, then narrowed by the active search query using the same
 * `filterOption` react-select itself applies. Range selection slices this
 * list so "everything between my two clicks" matches what's on screen.
 */
function getVisibleFlatOptions(selectProps: MyOptionProps["selectProps"]): any[] {
  const flat: any[] = [];
  for (const entry of (selectProps.options ?? []) as any[]) {
    if (entry && Array.isArray(entry.options)) flat.push(...entry.options);
    else flat.push(entry);
  }
  const { filterOption, getOptionLabel, getOptionValue, inputValue } = selectProps;
  if (!filterOption) return flat;
  return flat.filter((data) =>
    filterOption(
      { label: String(getOptionLabel(data)), value: String(getOptionValue(data)), data },
      inputValue ?? "",
    ),
  );
}

/**
 * Shift-click range selection: selects every visible option between the last
 * plainly-clicked option (the anchor) and the shift-clicked one, inclusive.
 * Returns true when the click was handled; false falls back to the normal
 * single-option toggle (no anchor yet, or either end isn't visible anymore —
 * e.g. the anchor got filtered out by a new search query).
 */
function selectShiftClickRange(props: MyOptionProps): boolean {
  const anchorValue = getShiftRangeAnchorRef(props.selectProps)?.current;
  if (anchorValue == null) return false;
  const { getOptionValue, isOptionDisabled } = props.selectProps;
  const visible = getVisibleFlatOptions(props.selectProps);
  const anchorIdx = visible.findIndex((o) => getOptionValue(o) === anchorValue);
  const targetIdx = visible.findIndex((o) => getOptionValue(o) === getOptionValue(props.data));
  if (anchorIdx === -1 || targetIdx === -1 || anchorIdx === targetIdx) return false;
  const [start, end] = anchorIdx < targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
  const selected = props.getValue();
  const selectedValues = new Set(selected.map((o) => getOptionValue(o)));
  const additions = visible
    .slice(start, end + 1)
    .filter((o) => !selectedValues.has(getOptionValue(o)) && !isOptionDisabled(o, selected));
  // The whole range is already selected — swallow the click rather than
  // letting it fall through and deselect the option under the cursor.
  if (additions.length > 0) {
    props.setValue([...selected, ...additions], "select-option", props.data);
  }
  return true;
}

/**
 * Keyboard counterpart to the shift-click machinery above. react-select (5.x)
 * already selects the keyboard-focused option on Enter (and on Space when the
 * search box is empty), but it has no notion of shift-extending a range. To add
 * it we need, at keydown time, the focused option plus the live `getValue` /
 * `setValue` / `selectProps`. The shared `Menu`/`Option` components have no
 * instance state, so the wrappers create this ref per select and thread it
 * through `selectProps`: `Menu` fills in the api each render, the focused
 * `Option` records itself.
 */
type ChipMultiKbdState = {
  focused: string | null;
  getValue: () => readonly any[];
  setValue: (value: any, action: any, option?: any) => void;
  selectProps: MyOptionProps["selectProps"];
};
type ChipMultiKbdRef = { current: ChipMultiKbdState | null };

function getChipMultiKbdRef(selectProps: unknown): ChipMultiKbdRef | undefined {
  return (selectProps as { chipMultiKbdRef?: ChipMultiKbdRef }).chipMultiKbdRef;
}

/** Toggle a single option (add when absent, remove when present). */
function toggleChipMultiOption(state: ChipMultiKbdState, option: any) {
  const { getOptionValue, isOptionDisabled } = state.selectProps;
  const value = getOptionValue(option);
  const selected = state.getValue();
  if (selected.some((o) => getOptionValue(o) === value)) {
    state.setValue(
      selected.filter((o) => getOptionValue(o) !== value),
      "deselect-option",
      option,
    );
  } else if (!isOptionDisabled(option, selected)) {
    state.setValue([...selected, option], "select-option", option);
  }
}

/**
 * Select every visible option between the anchor (last plain pick) and the
 * focused option, inclusive — the keyboard twin of `selectShiftClickRange`.
 * Falls back to a single toggle when there's no usable anchor (first pick, or
 * either end scrolled out of the current search results).
 */
function selectChipMultiRange(state: ChipMultiKbdState, anchorValue: string | null) {
  const { getOptionValue, isOptionDisabled } = state.selectProps;
  const visible = getVisibleFlatOptions(state.selectProps);
  const focusedOption = visible.find((o) => getOptionValue(o) === state.focused);
  if (!focusedOption) return;
  const anchorIdx =
    anchorValue == null ? -1 : visible.findIndex((o) => getOptionValue(o) === anchorValue);
  const targetIdx = visible.findIndex((o) => getOptionValue(o) === state.focused);
  if (anchorIdx === -1 || targetIdx === -1) {
    toggleChipMultiOption(state, focusedOption);
    return;
  }
  const [start, end] = anchorIdx < targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
  const selected = state.getValue();
  const selectedValues = new Set(selected.map((o) => getOptionValue(o)));
  const additions = visible
    .slice(start, end + 1)
    .filter((o) => !selectedValues.has(getOptionValue(o)) && !isOptionDisabled(o, selected));
  if (additions.length > 0) {
    state.setValue([...selected, ...additions], "select-option", focusedOption);
  }
}

/**
 * Adds shift+Enter / shift+Space range selection on top of react-select's
 * built-in keyboard selection, and records the focused option as the range
 * anchor after a plain keyboard pick (so a later shift+Enter or shift-click
 * extends from there, exactly like a plain mouse click does).
 */
function handleChipMultiKeyDown(
  event: KeyboardEvent<HTMLDivElement>,
  kbdRef: ChipMultiKbdRef,
  anchorRef: ShiftRangeAnchorRef,
) {
  const state = kbdRef.current;
  if (!state) return;
  const isEnter = event.key === "Enter";
  const isSpace = event.key === " " || event.key === "Spacebar";
  if (!isEnter && !isSpace) return;
  // Space while a query is typed must stay a literal space (multi-word search),
  // matching react-select's own behavior. Mid-search, Enter is the select key.
  if (isSpace && state.selectProps.inputValue) return;
  if (state.focused == null) return;

  if (event.shiftKey) {
    // Take over fully so react-select doesn't also toggle the single focused
    // option on top of the range we're about to apply.
    event.preventDefault();
    selectChipMultiRange(state, anchorRef.current);
    anchorRef.current = state.focused;
    return;
  }
  // Plain pick: let react-select toggle the focused option as usual, just
  // remember it as the anchor for a subsequent shift extension.
  anchorRef.current = state.focused;
}

/**
 * Per-instance refs + keydown handler for chip multi-selects, spread onto
 * react-select. The refs ride into `selectProps` (react-select forwards unknown
 * props); `onKeyDown` is only attached for chip multis so other selects keep
 * react-select's defaults untouched.
 */
function useChipMultiKeyboard(props: { variant?: "default" | "chip"; isMulti?: boolean | null }) {
  const shiftRangeAnchorRef = useRef<string | null>(null);
  const chipMultiKbdRef = useRef<ChipMultiKbdState | null>(null);
  const extras: Record<string, unknown> = { shiftRangeAnchorRef, chipMultiKbdRef };
  if (props.variant === "chip" && !!props.isMulti) {
    extras.onKeyDown = (event: KeyboardEvent<HTMLDivElement>) =>
      handleChipMultiKeyDown(event, chipMultiKbdRef, shiftRangeAnchorRef);
  }
  return extras;
}

export interface MyOptionProps extends OptionProps<any, boolean, GroupBase<any>> {
  data: any;
}
const Option = (props: MyOptionProps) => {
  // Chip-variant menus are portaled to `document.body` (see `chipPortalProps`)
  // so an ancestor `overflow-hidden` can't clip them. The downside: the menu
  // is no longer a DOM descendant of the text input, so a plain click on an
  // option blurs the input — react-select then fires `onMenuClose` and the
  // menu unmounts before the click can even select the value. For a multi
  // picker (e.g. "is any of" tag lists) this slams the dropdown shut after
  // every pick. Preventing the default on the option's `mousedown` keeps
  // focus in the input, so the menu stays open and the click still selects.
  const isPortaled = !!(props.selectProps as { menuPortalTarget?: unknown }).menuPortalTarget;
  const chipMulti = isChipMultiSelect(props.selectProps);
  let innerProps =
    isPortaled && chipMulti
      ? {
          ...props.innerProps,
          onMouseDown: (event: MouseEvent<HTMLDivElement>) => {
            event.preventDefault();
            props.innerProps.onMouseDown?.(event);
          },
        }
      : props.innerProps;
  if (chipMulti) {
    // react-select moves its "focused option" to whatever the pointer
    // touches, re-rendering the entire menu on every mousemove. With a few
    // hundred options that makes scrolling visibly janky. Drop the pointer
    // focus handlers and let CSS `hover:` paint the highlight instead —
    // keyboard navigation still drives `isFocused` as usual.
    const { onMouseMove: _move, onMouseOver: _over, onClick, ...rest } = innerProps;
    innerProps = {
      ...rest,
      onClick: (event: MouseEvent<HTMLDivElement>) => {
        // Click one option, then shift-click another to select everything
        // between them. A plain click records the anchor and toggles as usual.
        if (event.shiftKey && selectShiftClickRange(props)) return;
        const anchorRef = getShiftRangeAnchorRef(props.selectProps);
        if (anchorRef) anchorRef.current = props.selectProps.getOptionValue(props.data);
        onClick?.(event);
      },
    };
    // Record the keyboard-focused option so `handleChipMultiKeyDown` can
    // resolve it on Enter/Space (the `Menu` fills in the live api below).
    const kbdRef = getChipMultiKbdRef(props.selectProps);
    if (kbdRef?.current && props.isFocused) {
      kbdRef.current.focused = props.selectProps.getOptionValue(props.data);
    }
  }
  return (
    <components.Option {...props} innerProps={innerProps}>
      {props.data?.customer ? (
        <div className="flex items-center gap-2">
          <CustomerAvatar customer={props.data.customer} />
          <div className="flex flex-col">
            <span className="font-semibold text-sm">{getCustomerName(props.data.customer)}</span>
            <span className="text-xs text-muted-foreground">
              {formatPhoneNumber(props.data.customer.phoneNumber)}
            </span>
          </div>
        </div>
      ) : (
        <div className="space-between flex items-center">
          {isChipMultiSelect(props.selectProps) ? (
            <span
              aria-hidden
              className={cn(
                "mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
                props.isSelected
                  ? "border-foreground bg-foreground text-background"
                  : "border-muted-foreground/40 bg-background",
              )}
            >
              {props.isSelected ? <Check className="h-3 w-3" /> : null}
            </span>
          ) : null}
          {props.data?.type ? <TypeIcon type={props.data.type} /> : null}
          <span>{props.label}</span>
        </div>
      )}
    </components.Option>
  );
};

/**
 * Menu shell for chip multi-selects. Adds a small instructional header
 * (with a close button so the user can dismiss the always-open-on-select
 * menu without reaching for Escape) and a fixed footer of keyboard hints
 * (navigate / select / shift-range). The option list scrolls between the
 * two. Single selects and non-chip variants keep the default frame.
 */
const Menu = (props: MenuProps<any, boolean, GroupBase<any>>) => {
  // The menu unmounts on close — drop the shift-click anchor so the next
  // open starts fresh instead of range-selecting from a stale click.
  const anchorRef = getShiftRangeAnchorRef(props.selectProps);
  const kbdRefForCleanup = getChipMultiKbdRef(props.selectProps);
  useEffect(() => {
    return () => {
      if (anchorRef) anchorRef.current = null;
      if (kbdRefForCleanup) kbdRefForCleanup.current = null;
    };
  }, [anchorRef, kbdRefForCleanup]);

  const query = props.selectProps.inputValue ?? "";
  const isSearching = Boolean(props.selectProps.isLoading && query.trim());
  const searchStatus = isSearching ? <SearchLoadingStatus /> : null;
  // Marks the menu for `preventDismissOnDetachedLayer`: a portaled menu sits
  // outside the DOM subtree of any modal that hosts the control, so without
  // this a click on an option reads as an outside interaction and closes it.
  const menuProps = {
    ...props,
    innerProps: { ...props.innerProps, "data-select-menu": "" } as MenuProps<
      any,
      boolean,
      GroupBase<any>
    >["innerProps"],
  };
  // Portaled menus (chip variant) sit on `document.body`, outside any modal
  // that scroll-locks the page, so their wheel/touch scrolling has to be
  // shielded from the lock. Menus rendered inline don't need the guard.
  const isPortaled = !!(props.selectProps as { menuPortalTarget?: unknown }).menuPortalTarget;
  const withScrollGuard = (menu: React.ReactNode) =>
    isPortaled ? <MenuScrollGuard>{menu}</MenuScrollGuard> : menu;

  if (!isChipMultiSelect(props.selectProps)) {
    return withScrollGuard(
      <components.Menu {...menuProps}>
        {props.children}
        {searchStatus}
      </components.Menu>,
    );
  }
  // Keep the keyboard handler's view of the live api fresh (the focused
  // `Option` overwrites `focused` after this runs on each render).
  const kbdRef = getChipMultiKbdRef(props.selectProps);
  if (kbdRef) {
    kbdRef.current = {
      focused: kbdRef.current?.focused ?? null,
      getValue: props.getValue,
      setValue: props.setValue,
      selectProps: props.selectProps,
    };
  }
  const clearSearch = () => {
    if (!query) return;
    props.selectProps.onInputChange("", { action: "input-change", prevInputValue: query });
  };
  return withScrollGuard(
    <components.Menu {...menuProps}>
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-xs font-medium text-muted-foreground">
            {query ? <>Searching &ldquo;{query}&rdquo;</> : "Type to search"}
          </span>
          {query ? (
            <button
              type="button"
              aria-label="Clear search"
              className="shrink-0 text-xs font-medium text-primary hover:underline dark:text-brand-accent"
              // The menu is portaled; without this the mousedown blurs the
              // select input and unmounts the button before its click fires.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => clearSearch()}
            >
              Clear
            </button>
          ) : null}
        </div>
        <button
          type="button"
          aria-label="Close menu"
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          // Same blur guard as the clear button above.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            // Closing should leave a clean slate for the next open — the
            // programmatic `onMenuClose` doesn't reliably fire the input
            // reset, so clear the query explicitly first.
            clearSearch();
            props.selectProps.onMenuClose();
          }}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {props.children}
      {/* Shown under local matches while a live provider search is still in
          flight, so the menu doesn't look "done" before extra values arrive. */}
      {searchStatus}
      {/* Keyboard hints, mirroring the Add-criteria menu footer style. The
          shift hint advertises range selection (shift+Enter / shift+Space),
          the keyboard twin of shift-click. */}
      <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/20 px-3 h-9 text-[11px] text-muted-foreground">
        <span>
          <kbd className="font-mono">↑↓</kbd> navigate · <kbd className="font-mono">↵</kbd> select ·{" "}
          <kbd className="font-mono">⇧</kbd>+<kbd className="font-mono">↵</kbd> range
        </span>
        <span>
          <kbd className="font-mono">esc</kbd> close
        </span>
      </div>
    </components.Menu>,
  );
};

/**
 * Keeps a menu portaled to `document.body` scrollable inside a scroll-locked
 * modal (see `useScrollLockPassthrough`). `display: contents` leaves the guard
 * out of the layout box tree while still making it a DOM ancestor of the
 * option list, so scroll events bubble through it.
 */
function MenuScrollGuard({ children }: { children: React.ReactNode }) {
  return (
    <div ref={useScrollLockPassthrough<HTMLDivElement>()} style={{ display: "contents" }}>
      {children}
    </div>
  );
}

function SearchLoadingStatus() {
  return (
    <div
      className="flex items-center gap-2 border-t border-border bg-muted/30 px-3 h-9 text-[11px] text-muted-foreground"
      role="status"
      aria-live="polite"
    >
      <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
      Searching for more matches...
    </div>
  );
}

/**
 * Skeleton menu shown while the option set is still loading.
 *
 * Without this, react-select falls back to its default "Loading..." text
 * (or worse, renders the dropdown with `options=[]` and shows "No options"
 * during the in-flight window). Each skeleton row mirrors the real option
 * row's padding (`py-1.5 px-2`) so the menu height + cadence stays
 * identical when the data lands — no layout jump on resolve.
 */
const LoadingMessage = (props: NoticeProps<any, boolean, GroupBase<any>>) => {
  return (
    <components.LoadingMessage {...props}>
      <div className="flex flex-col" aria-label="Loading options" role="status">
        {[0, 1, 2, 3].map((idx) => (
          <div key={idx} className="mb-1">
            <div className="h-8 w-full rounded-md bg-muted/70 animate-pulse" />
          </div>
        ))}
      </div>
    </components.LoadingMessage>
  );
};

/**
 * Suppress react-select's default three-dot control spinner. Live search
 * progress is shown in the menu footer (`SearchLoadingStatus`) so we don't
 * also light up the control on every sample fetch (enum/curated pickers).
 */
const LoadingIndicator = () => null;

/**
 * Compact empty-state shown only when the option set has truly resolved to
 * an empty list (not during the in-flight fetch — that's `LoadingMessage`).
 * Tight padding + `text-xs` keeps the message proportional to the small
 * chip-cell dropdowns where it's most visible.
 */
const NoOptionsMessage = (props: NoticeProps<any, boolean, GroupBase<any>>) => {
  // Callers can override the empty-state copy via a `noOptionsLabel` prop on
  // StaticSelect (react-select forwards unknown props onto `selectProps`).
  // Defaults to the generic message so existing usages are unchanged.
  const selectProps = props.selectProps as {
    noOptionsLabel?: string;
    searchPromptLabel?: string;
    inputValue?: string;
  };
  const label = selectProps.noOptionsLabel;
  // Before the user types anything, async selects show this menu with an empty
  // option set. A "no matching options" message is misleading there — surface
  // an inviting search prompt instead when the caller opts in via
  // `searchPromptLabel`.
  const searchPrompt = selectProps.searchPromptLabel;
  const hasInput = Boolean((selectProps.inputValue ?? "").trim());
  // Chip-variant menus (segment editor) get a roomier, centered empty state
  // with a search icon so it reads as an intentional "nothing matched" panel
  // rather than a stray line of text. Other selects keep the compact row.
  if (isChipVariant(props.selectProps)) {
    return (
      <components.NoOptionsMessage {...props}>
        <div className="flex flex-col items-center gap-1.5 px-3 py-6 text-center text-xs text-muted-foreground">
          <SearchX aria-hidden className="h-5 w-5 opacity-50" />
          {label ?? "No matching options"}
        </div>
      </components.NoOptionsMessage>
    );
  }
  if (!hasInput && searchPrompt) {
    return (
      <components.NoOptionsMessage {...props}>
        <div className="flex flex-col items-center gap-1.5 px-3 py-6 text-center text-xs text-muted-foreground">
          <Search aria-hidden className="h-5 w-5 opacity-50" />
          {searchPrompt}
        </div>
      </components.NoOptionsMessage>
    );
  }
  return (
    <components.NoOptionsMessage {...props}>
      <div className="px-3 py-2 text-center text-xs text-muted-foreground">
        {label ?? "No matching options"}
      </div>
    </components.NoOptionsMessage>
  );
};

const MultiValueLabel = (props: MultiValueGenericProps) => {
  return (
    <components.MultiValueLabel {...props}>
      {props.data?.customer ? (
        <div className="flex items-center gap-2">
          <CustomerAvatar customer={props.data.customer} className="w-4 h-4" />
          <div className="flex flex-col">
            <span className="font-medium text-sm">{getCustomerName(props.data.customer)}</span>
          </div>
        </div>
      ) : (
        <span>{props.data.label}</span>
      )}
    </components.MultiValueLabel>
  );
};

const controlStyles = {
  base: "border rounded-md bg-background hover:cursor-pointer text-sm p-2",
  text: "border rounded-md bg-background hover:cursor-text text-sm p-2",
  focus: "border-ring ring-1 ring-ring",
  nonFocus: "border-border hover:border-border",
};
// In the `chip` variant the surrounding cell already paints focus + hover
// states for the whole row, so the control itself stays flush: no border,
// no rounded inset, no ring. Anything else creates a visible inner box that
// fights with the chip cell's outline.
const chipControlStyles = {
  base: "border-0 rounded-none bg-transparent hover:cursor-pointer text-[13px] px-2 py-0 min-h-9 w-full",
  text: "border-0 rounded-none bg-transparent hover:cursor-text text-[13px] px-2 py-0 min-h-9 w-full",
  focus: "",
  nonFocus: "",
};
const placeholderStyles = "text-muted-foreground";
const valueContainerStyles = "flex flex-wrap gap-y-0.5";
const multiValueStyles =
  "bg-muted/60 rounded items-center py-0.5 pl-2 pr-1 gap-1.5 text-[13px] m-0.5 border border-border/60";
const multiValueRemoveStyles = "hover:bg-muted hover:text-foreground rounded-full p-0.5";
const indicatorsContainerStyles = "gap-1";
const loadingIndicatorStyles = "text-muted-foreground";
const clearIndicatorStyles = "cursor-pointer";
const menuStyles =
  "mt-1 rounded-lg border bg-popover text-popover-foreground shadow-lg ring-1 ring-black/5 outline-none";
// Fixed width: `w-max` would track the widest *visible* option, so the menu
// width jumped around as typing filtered the list. Long labels wrap instead.
const chipMenuStyles = cn(menuStyles, "!w-[380px]");
// Must match the width in `chipMenuStyles`; the portal offset is computed from
// it in `getStyles` (a portaled menu is wider than its control, so the control
// rect alone can't tell us where the menu's right edge lands).
const chipMenuWidth = 380;
// Breathing room between a portaled menu and the viewport edge.
const menuViewportMargin = 8;
const menuListStyles = "p-1 max-h-[280px] overflow-y-auto";
// Grouped menus drop the inset padding so square option rows meet the
// heading rules and the menu edges. Ungrouped menus keep `p-1`.
const groupedMenuListStyles = "max-h-[280px] overflow-y-auto";
// Later groups get a top rule so the heading does not sit flush against
// the previous group's last option. The first group stays flush with the
// menu chrome.
const groupStyles = "[&:not(:first-child)>:first-child]:border-t";
const groupHeadingStyles = "sticky top-0 z-10 border-b border-border bg-popover px-3 py-1.5";
const optionStyles = {
  base: "hover:cursor-pointer rounded cursor-default select-none rounded-md py-1.5 px-2 !text-sm font-medium outline-none text-foreground",
  focus: "bg-muted active:bg-muted",
  selected: "text-foreground",
};
const chipOptionStyles = {
  // `hover:bg-muted` (not react-select's focus-follows-pointer) paints the
  // pointer highlight — see the chip-multi mousemove note in `Option`.
  base: "hover:cursor-pointer rounded cursor-default select-none rounded-md py-1.5 px-2 !text-sm font-normal outline-none text-foreground hover:bg-muted",
  focus: "bg-muted active:bg-muted",
  selected: "text-foreground font-medium",
};
// Lets the browser skip layout + paint for off-screen option rows, which
// keeps big multi lists (hundreds of sampled tag values) scrolling smoothly.
// The intrinsic size matches the real option row height (20px line + 12px
// vertical padding) so the scrollbar doesn't jump as rows materialize.
const chipMultiOptionPerfStyles = "[content-visibility:auto] [contain-intrinsic-size:auto_32px]";

function getStyles(variant?: "default" | "chip"): StylesConfig {
  return {
    input: (base: CSSObjectWithLabel) => ({
      ...base,
      input: {
        cursor: "text",
      },
      "input:focus": {
        boxShadow: "none",
      },
    }),
    // Portal-rendered menu must live above all popovers; Radix Popover uses
    // z-50, so we go higher. It also mounts on `document.body`, which Radix
    // marks `pointer-events: none` while a modal is open, so the menu has to
    // re-enable pointer events to stay clickable inside a dialog or sheet.
    menuPortal: (base: CSSObjectWithLabel, props) => {
      const style = { ...base, zIndex: 100, pointerEvents: "auto" as const };
      if (variant !== "chip" || typeof document === "undefined") return style;
      // react-select aligns the portal with the control's left edge. A chip
      // control narrower than `chipMenuWidth` (the flow inspector's cells are
      // ~350px) therefore hangs the menu past the right edge of the viewport,
      // which makes the page horizontally scrollable for as long as the menu
      // is open. On a display with classic (space-taking) scrollbars that
      // shrinks the viewport, which moves the control, which repositions the
      // menu: react-select re-measures synchronously on every commit, so the
      // scrollbar flapping in and out becomes an unbounded render loop
      // ("Maximum update depth exceeded"). Keeping the menu inside the
      // viewport removes the overflow that starts it.
      const left = (props as { rect: { left: number } }).rect.left;
      const maxLeft = document.documentElement.clientWidth - chipMenuWidth - menuViewportMargin;
      return { ...style, left: Math.max(menuViewportMargin, Math.min(left, maxLeft)) };
    },
  };
}

function chipPortalProps(variant: "default" | "chip" | undefined) {
  if (variant !== "chip") return {};
  if (typeof window === "undefined") return {};
  // Render the menu in a portal so ancestor `overflow-hidden` (chip
  // container, popover wrappers) cannot clip the dropdown. Fixed positioning
  // keeps the menu out of the document's scrollable area entirely, so it can
  // never grow the page the way an absolutely positioned portal can.
  return { menuPortalTarget: document.body, menuPosition: "fixed" as const };
}

/**
 * Keep a multi-select chip menu open while the user picks several values.
 *
 * Used by the segment condition pickers (e.g. an "is any of" tag list), where
 * the whole point is choosing many values in one pass. react-select's default
 * `closeMenuOnSelect` is `true`, which slams the menu shut after every pick
 * and forces the user to re-open + re-find their place. Setting it `false`
 * (plus not blurring the input and not hiding already-picked options) lets the
 * list stay put across selections. Callers can still override any of these.
 *
 * Scoped to the `chip` variant so app-wide multi-selects keep their existing
 * select-and-close behavior.
 */
function multiSelectKeepOpenProps(props: {
  variant?: "default" | "chip";
  isMulti?: boolean | null;
  closeMenuOnSelect?: boolean;
  blurInputOnSelect?: boolean;
  hideSelectedOptions?: boolean;
}) {
  if (props.variant !== "chip" || !props.isMulti) return {};
  return {
    closeMenuOnSelect: props.closeMenuOnSelect ?? false,
    blurInputOnSelect: props.blurInputOnSelect ?? false,
    hideSelectedOptions: props.hideSelectedOptions ?? false,
  };
}

/**
 * Keep the typed search query in place across selections on chip
 * multi-selects. react-select wipes `inputValue` after every pick (the
 * `set-value` input action), which forces re-typing the filter between
 * picks in a keep-open menu. Controlling the input and ignoring
 * `set-value` preserves the query until the menu closes.
 *
 * No-ops (returns `{}`) for non-chip / single selects, and for callers
 * that already control `inputValue` / `onInputChange` themselves.
 */
function useChipMultiSearchPersistence(props: {
  variant?: "default" | "chip";
  isMulti?: boolean | null;
  inputValue?: string;
  onInputChange?: unknown;
}) {
  const [inputValue, setInputValue] = useState("");
  if (props.variant !== "chip" || !props.isMulti) return {};
  if (props.inputValue !== undefined || props.onInputChange) return {};
  return {
    inputValue,
    onInputChange: (next: string, meta: InputActionMeta) => {
      if (meta.action === "input-change") {
        setInputValue(next);
      } else if (meta.action === "menu-close" || meta.action === "input-blur") {
        // Reset so the next open starts with a clean search.
        setInputValue("");
      }
      // `set-value` (fires with "" after each pick) is deliberately ignored.
    },
  };
}

function hasOptionGroups(options: unknown): boolean {
  return (
    Array.isArray(options) &&
    options.some((item) => item != null && typeof item === "object" && "options" in item)
  );
}

function getClassName(props: {
  hideDropdownIndicator?: boolean;
  variant?: "default" | "chip";
  isMulti?: boolean;
  menuClassName?: string;
  options?: unknown;
}): ClassNamesConfig {
  const variant = props.variant ?? "default";
  const grouped = hasOptionGroups(props.options);
  const cs = variant === "chip" ? chipControlStyles : controlStyles;
  const os = variant === "chip" ? chipOptionStyles : optionStyles;
  return {
    control: ({ isFocused }: { isFocused: boolean }) =>
      cn(isFocused ? cs.focus : cs.nonFocus, cs.text),
    valueContainer: () => valueContainerStyles,
    placeholder: () => placeholderStyles,
    indicatorsContainer: () => indicatorsContainerStyles,
    clearIndicator: () => clearIndicatorStyles,
    dropdownIndicator: () => (props.hideDropdownIndicator ? "!hidden" : ""),
    loadingIndicator: () => loadingIndicatorStyles,
    menu: () => cn(variant === "chip" ? chipMenuStyles : menuStyles, props.menuClassName),
    menuList: () => (grouped ? groupedMenuListStyles : menuListStyles),
    group: () => groupStyles,
    groupHeading: () => groupHeadingStyles,
    option: ({ isFocused, isSelected }: { isFocused: boolean; isSelected: boolean }) =>
      cn(
        isFocused && os.focus,
        isSelected && os.selected,
        os.base,
        grouped && "!rounded-none",
        variant === "chip" && props.isMulti && chipMultiOptionPerfStyles,
      ),
    multiValue: () => multiValueStyles,
    multiValueRemove: () => multiValueRemoveStyles,
    // The custom `LoadingMessage` / `NoOptionsMessage` components own their
    // own padding so the outer wrapper stays flush. Without explicit
    // overrides, react-select's defaults can leak in when `unstyled` is
    // toggled on certain pages and re-introduce the original chunky
    // empty-state row.
    loadingMessage: () => "",
    noOptionsMessage: () => "",
  };
}

export interface SelectProps extends Props {
  as?: "Creatable" | "Async" | "Select";
  hideDropdownIndicator?: boolean;
  formatCreateLabel?: (inputValue: string) => string;
  /** Keep creatable "Use …" available while `isLoading` is true. */
  allowCreateWhileLoading?: boolean;
  /** Compact chip-cell rendering (no border, no internal padding) */
  variant?: "default" | "chip";
  /** Overrides the dropdown empty-state copy (default "No matching options"). */
  noOptionsLabel?: string;
  /**
   * Render group headings as plain category labels (no integration icon, count
   * badge, or start-casing). Use when group labels are human category names
   * rather than integration provider ids.
   */
  plainGroupHeading?: boolean;
  /**
   * Observe the typed search text without taking over `inputValue` /
   * `onInputChange` (which would disable the chip-multi search persistence).
   * Fires with the query on each keystroke and with "" on menu close, so
   * callers can drive server-side searches when the option list is capped.
   */
  onSearchChange?: (search: string) => void;
}
export function StaticSelect(props: SelectProps) {
  const portal = chipPortalProps(props.variant);
  const searchPersistence = useChipMultiSearchPersistence(props);
  // Compose the external search observer with whichever `onInputChange`
  // applies (persistence hook or caller's own): react-select accepts one.
  const onSearchChange = props.onSearchChange;
  const innerOnInputChange =
    "onInputChange" in searchPersistence ? searchPersistence.onInputChange : props.onInputChange;
  const searchObserver = onSearchChange
    ? {
        onInputChange: (next: string, meta: InputActionMeta) => {
          if (meta.action === "input-change") {
            onSearchChange(next);
          } else if (meta.action === "menu-close") {
            onSearchChange("");
          }
          innerOnInputChange?.(next, meta);
        },
      }
    : {};
  // Passed as a spread (react-select only types its own props on the JSX
  // element; unknown props ride along on spreads and land in `selectProps`).
  // Includes the shift-range anchor + focused-option refs and the chip-multi
  // `onKeyDown`; spread last so the handler wins over react-select's default.
  const keyboardProps = useChipMultiKeyboard(props);
  if (props.as === "Creatable") {
    return (
      <Creatable
        unstyled
        components={{
          ClearIndicator,
          GroupHeading,
          Menu,
          Option,
          LoadingMessage,
          LoadingIndicator,
          NoOptionsMessage,
        }}
        formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
        classNames={getClassName(props)}
        styles={getStyles(props.variant)}
        {...portal}
        {...props}
        {...multiSelectKeepOpenProps(props)}
        {...searchPersistence}
        {...searchObserver}
        {...keyboardProps}
      />
    );
  } else {
    return (
      <Select
        unstyled
        components={{
          ClearIndicator,
          DropdownIndicator,
          MultiValueRemove,
          GroupHeading,
          Menu,
          Option,
          LoadingMessage,
          LoadingIndicator,
          NoOptionsMessage,
        }}
        classNames={getClassName(props)}
        styles={getStyles(props.variant)}
        {...portal}
        {...props}
        {...multiSelectKeepOpenProps(props)}
        {...searchPersistence}
        {...searchObserver}
        {...keyboardProps}
      />
    );
  }
}

export interface AsyncSelectProps extends AsyncProps<any, boolean, GroupBase<any>> {
  hideDropdownIndicator?: boolean;
  formatCreateLabel?: (inputValue: string) => string;
  variant?: "default" | "chip";
  OptionComponent?: (option: OptionProps<any, boolean, GroupBase<any>>) => React.ReactNode;
  /** Overrides the dropdown empty-state copy when a search returns nothing. */
  noOptionsLabel?: string;
  /** Inviting prompt shown in the menu before the user has typed a query. */
  searchPromptLabel?: string;
  /** Extra classes for the dropdown menu (e.g. a max-width cap). */
  menuClassName?: string;
}
export function AsyncSelect(props: AsyncSelectProps) {
  const portal = chipPortalProps(props.variant);
  const searchPersistence = useChipMultiSearchPersistence(props);
  // See note in StaticSelect — spread so it rides into `selectProps`.
  const keyboardProps = useChipMultiKeyboard(props);
  return (
    <Async
      unstyled
      components={{
        ClearIndicator,
        DropdownIndicator,
        MultiValueRemove,
        MultiValueLabel,
        GroupHeading,
        Menu,
        Option,
        LoadingMessage,
        LoadingIndicator,
        NoOptionsMessage,
      }}
      classNames={getClassName(props)}
      styles={getStyles(props.variant)}
      {...portal}
      {...props}
      {...multiSelectKeepOpenProps(props)}
      {...searchPersistence}
      {...keyboardProps}
    />
  );
}
